import { useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, cn } from '@atrvd/saas-ui'
import { formatCategoryAmount, formatDate } from '@dcp/shared/utils/formatters'
import type { AssignableCategory, BankTransaction } from '@/types/banking'

/** Longest description fragment kept in the control's accessible name. */
const A11Y_DESCRIPTION_MAX = 60

/**
 * Accessible name of a row's Category control (DCP-123).
 *
 * The raw description alone is a poor name: bank feeds pad it with
 * field-alignment spaces up to ~120 chars, and it is often not unique ("CHECK
 * PAID" on many rows), so a screen-reader user could not tell the controls
 * apart. Date + amount + a whitespace-collapsed, capped description is what a
 * sighted user uses to identify the row.
 *
 * @param txn - The row the control belongs to.
 * @returns e.g. "Category for 09/30/2026, ($9.30), CD debit 273"
 */
function categoryControlLabel(txn: BankTransaction): string {
  const description = txn.description.replace(/\s+/g, ' ').trim()
  const short =
    description.length > A11Y_DESCRIPTION_MAX
      ? `${description.slice(0, A11Y_DESCRIPTION_MAX).trimEnd()}…`
      : description
  const amount = formatCategoryAmount(txn.amount, txn.categoryType).text
  return `Category for ${formatDate(txn.date)}, ${amount}, ${short}`
}

interface TransactionCategorySelectProps {
  /** Row being categorised — supplies the current `categoryId`, its label and the a11y name. */
  transaction: BankTransaction
  /** Categories the user can assign (the page's assignable list). */
  categories: AssignableCategory[]
  /**
   * Persist the picked category for this row. Resolves on success, rejects on
   * failure — the mutation hook behind it already toasts the error and rolls
   * back its optimistic cache update, so the caller owns no error UI.
   */
  onAssign: (category: AssignableCategory) => Promise<void>
}

/**
 * Amber "needs a category" look for an EMPTY control (DCP-123, AC#1), mapped
 * from the prototype (#fef9c3 fill, #f59e0b border, #92400e semibold text) onto
 * the Tailwind palette, with dark variants in the same family the codebase uses
 * for its amber notices (UpdateLocAvailabilityDialog / ViewBankTransactionsDialog).
 * The placeholder text inherits the colour: Radix renders it as a plain span, so
 * the trigger's own `placeholder:` utility never applies to it.
 */
const EMPTY_TRIGGER_CLASS =
  'border-amber-500 bg-yellow-100 font-semibold text-amber-800 dark:border-amber-500/70 dark:bg-amber-950/40 dark:text-amber-200'

/**
 * Always-visible inline Category dropdown for a Daily Banking row (DCP-123,
 * AC#2 / AC#3): picking an option saves immediately, with no confirm step.
 *
 * Built from the saas-ui Select composition rather than `SelectSimple` because
 * the trigger has to be styled amber when empty, and `SelectSimple` exposes no
 * trigger className. The DataTable's own inline editing (`editConfig: 'select'`)
 * was not used either: it is click-to-edit with save/cancel buttons, which is
 * exactly the extra step the prototype removes.
 *
 * The control is fully controlled by `transaction.categoryId`: the assign
 * mutation updates the React Query cache optimistically, so the new value — and
 * with it the disappearing row highlight — shows up on the next render, and a
 * failed save rolls the cache (and therefore this control) back by itself.
 *
 * @returns The Select for the row's Category cell.
 */
export function TransactionCategorySelect({
  transaction,
  categories,
  onAssign,
}: TransactionCategorySelectProps) {
  const { categoryId } = transaction

  /**
   * The row's current category may be absent from the assignable list (a
   * legacy or since-retired category). Radix only renders a value for which an
   * item exists, so without an item of its own the cell would read as empty —
   * and, worse, look "uncategorized" while it is not. It is kept as a DISABLED
   * item: visible as the current value, but not offered as a new choice.
   */
  const orphan = useMemo(
    () =>
      categoryId !== null && !categories.some((c) => c.id === categoryId)
        ? { id: categoryId, name: transaction.category }
        : null,
    [categoryId, categories, transaction.category]
  )

  /**
   * Map the picked id back to its category and save it. Picking the current
   * value is a no-op in Radix, so every call here is a real change. The
   * rejection is swallowed on purpose: the hook has already toasted it and
   * restored the previous value, and an unhandled rejection would only add noise.
   *
   * @param id - Id of the picked category.
   */
  const handleValueChange = (id: string) => {
    const category = categories.find((c) => c.id === id)
    if (!category) return
    onAssign(category).catch(() => undefined)
  }

  const isEmpty = categoryId === null

  return (
    // `''` (not undefined) keeps the Select controlled while showing the
    // placeholder — Radix treats an empty-string value as "nothing selected".
    <Select value={categoryId ?? ''} onValueChange={handleValueChange}>
      <SelectTrigger
        aria-label={categoryControlLabel(transaction)}
        className={cn('h-9 w-[180px] text-[13px]', isEmpty && EMPTY_TRIGGER_CLASS)}
      >
        <SelectValue placeholder="Select category" />
      </SelectTrigger>
      <SelectContent>
        {orphan && (
          <SelectItem value={orphan.id} disabled>
            {orphan.name}
          </SelectItem>
        )}
        {categories.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
